/**
 * @file Where to send someone after they sign in, and the enquiry or booking
 * request draft that waits for them while they do.
 *
 * A guest writing an enquiry on a storefront signs in mid-thought. The login
 * and signup pages read `?next=` so the guest lands back on the package with
 * the sheet reopened, instead of on /my-apps (tech design §9.4).
 *
 * A host works the same way from the other side: the link in an enquiry email
 * opens `/host/inbox/<id>`, a signed-out host is sent to `/host/login` with
 * that path as `next`, and signing in opens the enquiry itself
 * (host-inbox-tech-design §4.5.10, §9.2). A guest's "New message" email opens
 * `/messages/<id>` the same way, through the member login
 * (host-messaging-tech-design §8.2).
 *
 * `next` arrives in a URL anyone can craft, so it is an open-redirect vector
 * unless it is checked. `readSafeNext` accepts only a same-origin relative path
 * under an allowlisted prefix; anything else reads as "no next", and every
 * caller then does exactly what it did before this file existed.
 *
 * Pure and dependency-free on purpose: no React, no Vite aliases, so the
 * security cases can be exercised with plain node.
 *
 * Plain .js: no JSX.
 */

/**
 * The only places a `next` may send someone. Storefront pages are the reason
 * this exists; public guides have the same missing return-to and can adopt it.
 * `/host/inbox` has no trailing slash because the inbox itself is a target as
 * well as the enquiries under it; `isUnderPrefix` keeps it to that segment.
 * `/host/calendar` (host-availability P2) is the same, with its month in the
 * query: `/host/calendar?month=2026-12`.
 */
export const NEXT_PREFIXES = ['/hosts/', '/guides/', '/host/inbox', '/host/calendar', '/messages'];

const DRAFT_KEY_PREFIX = 'dt.enquiryDraft.';
const BOOKING_DRAFT_KEY_PREFIX = 'dt.bookingDraft.';
/** Both drafts last a day: long enough for a sign-up and a verification code. */
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const currentOrigin = () => (typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost');

/**
 * Whether `pathname` sits under `prefix`. A prefix ending in `/` matches
 * anything that starts with it; one without matches itself or what follows a
 * `/` after it, so `/host/inbox` accepts `/host/inbox/1042` but never
 * `/host/inboxes`.
 */
const isUnderPrefix = (pathname, prefix) =>
  prefix.endsWith('/') ? pathname.startsWith(prefix) : pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * The `next` path from a query string, if it is safe to navigate to; else null.
 *
 * Safe means all of:
 *  - it starts with a single `/` (not `//host` or `/\host`, which browsers
 *    treat as protocol-relative, and not `javascript:` or `https://`);
 *  - it resolves to this origin once parsed as a URL;
 *  - its path is under one of NEXT_PREFIXES (`isUnderPrefix`).
 *
 * @param {string} search `location.search`, with or without the leading `?`.
 * @param {string} [origin] This page's origin; defaults to `window.location.origin`.
 * @returns {string|null} The path plus its query and hash, e.g.
 *   `/hosts/manila/packages/weekend-31?enquire=1`.
 */
export const readSafeNext = (search, origin = currentOrigin()) => {
  let raw;
  try {
    raw = new URLSearchParams(search ?? '').get('next');
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
  // Control characters and backslashes anywhere are never part of a real path.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\\]/.test(raw)) return null;

  let url;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin !== new URL(origin).origin) return null;
  if (!NEXT_PREFIXES.some((prefix) => isUnderPrefix(url.pathname, prefix))) return null;

  return `${url.pathname}${url.search}${url.hash}`;
};

/** `path` with `next` appended as a query parameter; `path` alone when there is no next. */
export const withNext = (path, next) => {
  if (!next) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}next=${encodeURIComponent(next)}`;
};

/**
 * Whether a freshly signed-in account has to finish member onboarding before
 * it can use app features — the same rule as the sidebar's `cannotUseAppFeats`.
 * A NULL user type is treated as a member, as the sidebar does.
 */
export const mustOnboard = (data) =>
  (data?.userType === null || data?.userType === undefined || data?.userType === 'member') && !data?.isOTPVerified;

/**
 * Where a login page sends a signed-in account when there is a safe `next`.
 * An account that still has to verify goes through onboarding first, carrying
 * `next` so it lands on the package afterwards.
 */
export const landingWithNext = (data, next) => (mustOnboard(data) ? withNext('/onboarding', next) : next);

// ---------------------------------------------------------------------------
// The drafts: an enquiry, and a booking request (host-availability P3b)
// ---------------------------------------------------------------------------

/**
 * sessionStorage rather than localStorage: a draft can hold health details and
 * devices get shared, but it still survives the same-tab redirect through
 * /login. Every access is guarded — private windows and blocked storage throw.
 */
const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const stashDraft = (prefix, packageId, draft) => {
  const store = storage();
  if (!store || !packageId) return false;
  try {
    store.setItem(`${prefix}${packageId}`, JSON.stringify({ ...draft, saved_at: Date.now() }));
    return true;
  } catch {
    return false;
  }
};

const restoreDraft = (prefix, packageId, now) => {
  const store = storage();
  if (!store || !packageId) return null;
  const key = `${prefix}${packageId}`;
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || typeof draft !== 'object' || !draft.saved_at || now - draft.saved_at > DRAFT_MAX_AGE_MS) {
      store.removeItem(key);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
};

const clearDraft = (prefix, packageId) => {
  const store = storage();
  if (!store || !packageId) return;
  try {
    store.removeItem(`${prefix}${packageId}`);
  } catch {
    // Nothing to do: an unreadable store has nothing to clear.
  }
};

/**
 * Keep an unsent enquiry while the guest signs in.
 *
 * `draft` holds the form (`message`, `preferred_start_date`, `num_guests`) plus
 * the context the login page's banner shows (`host_name`, `package_title`).
 */
export const stashEnquiryDraft = (packageId, draft) => stashDraft(DRAFT_KEY_PREFIX, packageId, draft);

/** The stashed draft for a package, or null when there is none or it is over a day old. */
export const restoreEnquiryDraft = (packageId, now = Date.now()) => restoreDraft(DRAFT_KEY_PREFIX, packageId, now);

/** Forget the draft once it has been sent. */
export const clearEnquiryDraft = (packageId) => clearDraft(DRAFT_KEY_PREFIX, packageId);

/**
 * Keep an unsent booking request while the guest signs in (or verifies).
 *
 * `draft` holds the request (`start_date`, `num_guests`, `message`), its
 * idempotency key (`client_id`, and `sent_key`, the payload it was last sent
 * with), and the login banner's context (`host_name`, `package_title`). See
 * views/host-storefront/request/request-format.js `toBookingDraft`. Its own
 * key, so an enquiry and a request about one package never overwrite each
 * other.
 */
export const stashBookingDraft = (packageId, draft) => stashDraft(BOOKING_DRAFT_KEY_PREFIX, packageId, draft);

/** The stashed request for a package, or null when there is none or it is over a day old. */
export const restoreBookingDraft = (packageId, now = Date.now()) => restoreDraft(BOOKING_DRAFT_KEY_PREFIX, packageId, now);

/** Forget the request once it has been sent. */
export const clearBookingDraft = (packageId) => clearDraft(BOOKING_DRAFT_KEY_PREFIX, packageId);

/**
 * The package a `next` path points at, when it is a storefront package page:
 * `{ hostSlug, packageId }`, or null. Used by the login banner.
 */
export const storefrontPackageOf = (next) => {
  const match = /^\/hosts\/([a-z0-9-]+)\/packages\/[^/?#]*-(\d+)(?:[/?#]|$)/.exec(next ?? '');
  return match ? { hostSlug: match[1], packageId: Number(match[2]) } : null;
};

/** Whether a `next` path opens the guest's Messages (the login banner's note). */
export const isMessagesTarget = (next) => /^\/messages(?:\/\d+)?\/?(?:[?#]|$)/.test(next ?? '');

/**
 * What a `next` path opens in the host inbox, for the host login's banner:
 * `{ target: 'enquiry', enquiryId }` for `/host/inbox/1042`, `{ target: 'inbox' }`
 * for the inbox itself, or null for anything else, a malformed id included.
 */
export const hostInboxTargetOf = (next) => {
  const match = /^\/host\/inbox(?:\/(\d+))?\/?(?:[?#]|$)/.exec(next ?? '');
  if (!match) return null;
  return match[1] ? { target: 'enquiry', enquiryId: Number(match[1]) } : { target: 'inbox' };
};

/**
 * Whether a `next` path reopens a package's request sheet (`?request=1`), for
 * the login banner's booking words. Reads the query only; the path is
 * `storefrontPackageOf`'s to check.
 */
export const isBookingRequestNext = (next) => {
  const query = /^[^?#]*\?([^#]*)/.exec(next ?? '');
  if (!query) return false;
  try {
    return new URLSearchParams(query[1]).get('request') === '1';
  } catch {
    return false;
  }
};
